import { Injectable, inject } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom, map } from 'rxjs';
import { Client, Connections, Profile } from '../../api/api-clients';
import { ProfileConverter } from '../converters/profile.converter';
import type { AppProfile } from '../models/profile.model';
import type { AppConnections } from '../models/connections.model';
import type { AppSearchResult } from '../models/search-result.model';
import { connectionKeys, explorerKeys } from './query-keys';

@Injectable({ providedIn: 'root' })
export class ConnectionsService {
  private readonly client = inject(Client);
  private readonly queryClient = injectQueryClient();

  readonly connections = injectQuery(() => ({
    queryKey: connectionKeys.mine(),
    queryFn: () =>
      firstValueFrom(this.client.connectionsGET().pipe(map(toAppConnections))),
  }));

  readonly follow = injectMutation(() => ({
    mutationFn: (id: string) => firstValueFrom(this.client.connectionsPOST(id)),
    onMutate: async (id) => {
      await this.queryClient.cancelQueries({ queryKey: connectionKeys.mine() });
      const previous = this.queryClient.getQueryData<AppConnections>(connectionKeys.mine());

      // Try to get the full profile from explorer detail cache
      const cachedResult = this.queryClient.getQueryData<AppSearchResult>(
        explorerKeys.detail(id),
      );

      if (previous && cachedResult) {
        this.queryClient.setQueryData<AppConnections>(connectionKeys.mine(), {
          ...previous,
          following: [...previous.following, cachedResult.profile],
        });
      }

      return { previous };
    },
    onSuccess: (updated) => {
      this.queryClient.setQueryData(connectionKeys.mine(), toAppConnections(updated));
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        this.queryClient.setQueryData(connectionKeys.mine(), context.previous);
      }
    },
    onSettled: () => {
      this.queryClient.invalidateQueries({ queryKey: connectionKeys.mine() });
    },
  }));

  readonly unfollow = injectMutation(() => ({
    mutationFn: (id: string) => firstValueFrom(this.client.connectionsDELETE(id)),
    onMutate: async (id) => {
      await this.queryClient.cancelQueries({ queryKey: connectionKeys.mine() });
      const previous = this.queryClient.getQueryData<AppConnections>(connectionKeys.mine());

      if (previous) {
        this.queryClient.setQueryData<AppConnections>(connectionKeys.mine(), {
          ...previous,
          following: previous.following.filter((p) => p.id !== id),
        });
      }

      return { previous };
    },
    onSuccess: (updated) => {
      this.queryClient.setQueryData(connectionKeys.mine(), toAppConnections(updated));
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        this.queryClient.setQueryData(connectionKeys.mine(), context.previous);
      }
    },
    onSettled: () => {
      this.queryClient.invalidateQueries({ queryKey: connectionKeys.mine() });
    },
  }));
}

function safeToAppProfile(profile: Profile): AppProfile | null {
  try {
    return ProfileConverter.toAppModel(profile);
  } catch {
    return null;
  }
}

export function toAppConnections(connections: Connections): AppConnections {
  return {
    following: (connections.following ?? [])
      .map(safeToAppProfile)
      .filter((p): p is AppProfile => p !== null),
    followers: (connections.followers ?? [])
      .map(safeToAppProfile)
      .filter((p): p is AppProfile => p !== null),
  };
}
